import React, { createContext, useState, useContext, useEffect } from 'react';
import { db, auth } from '../firebaseConfig';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  orderBy, 
  limit, 
  addDoc, 
  serverTimestamp,
  doc,
  updateDoc
} from 'firebase/firestore';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef, 
      where('recipientId', '==', user.uid),
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newNotifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setNotifications(newNotifications);
      setUnreadCount(newNotifications.length);
    });

    return () => unsubscribe();
  }, []);

  const markNotificationAsRead = async (notificationId) => {
    try {
      if (!auth.currentUser) return;

      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true
      });

      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true } 
            : notification
        )
      );

      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const clearAllNotifications = async () => {
    // Implement clearing all notifications
  };

  const createNotification = async (recipientId, type, message, relatedId) => {
    if (!recipientId) return;

    try {
      const notificationRef = collection(db, 'notifications');
      await addDoc(notificationRef, {
        recipientId,
        type,
        message,
        relatedId,
        read: false,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  const markNotificationAsSeen = async (notificationId) => {
    try {
      if (!auth.currentUser) return;

      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        seen: true
      });

      // Update local state if needed
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, seen: true } 
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as seen:', error);
    }
  };

  const contextValue = {
    notifications,
    unreadCount,
    markNotificationAsRead,
    clearAllNotifications,
    createNotification,
    markNotificationAsSeen
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);